/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import * as Sentry from "@sentry/browser";
import Str from "shared/components/Str";
import ChecklistSvg from "shared/components/svgs/checklist.svg";
import useUserGuide from "shared/hooks/useUserGuide";
import useLockBodyScroll from "shared/hooks/useLockBodyScroll";
import {
	trackDismissedGlobalPreferenceModal,
	trackShownGlobalPreferenceEntryPoint,
	ENTRY_POINTS,
	VIEWDED_GP_SOURCES,
} from "analytics/globalPreferences";
import { getGlobalPreferencesUrl } from "shared/util/globalPreferences";
import { useRefluxPassCount, canShowGlobalPreferenceTooltip } from "./helpers";

import "./index.scss";

if (process.env.PLATFORM === "mobile") {
	require("./index.mobile.scss");
}

const USER_GUIDE_NAME = "GLOBAL_PREFERENCE_DOUBLETAKE_NAG";
const NUMBER_OF_PASSES_BEFORE_SHOW = 3;
const GP_SOURCE = VIEWDED_GP_SOURCES.DT_MODAL;

const useTracking = (show) => {
	useEffect(() => {
		if (show) {
			trackShownGlobalPreferenceEntryPoint(ENTRY_POINTS.DT_MODAL);
		}
	}, [show]);
};

const withFadeIn = (content) => (
	<CSSTransition
		in
		appear
		classNames="gp-tooltip-transition"
		unmountOnExit
		timeout={300}
	>
		{content}
	</CSSTransition>
);

function GlobalPreferenceTooltip({ passCount }) {
	const location = useLocation();
	const canShow = canShowGlobalPreferenceTooltip(location.pathname);
	const [dismissed, setDismissed] = useState(false);
	const {
		hasSeenUserGuide,
		userGuideResponse: { loading, error },
		markUserGuideAsSeen,

		// no-cache in case the user clicks ok and then comes back
	} = useUserGuide(USER_GUIDE_NAME, {
		fetchPolicy: "no-cache",
		skip: !canShow,
	});
	const refluxPassCount = useRefluxPassCount();

	const votedEnough =
		passCount >= NUMBER_OF_PASSES_BEFORE_SHOW
		|| refluxPassCount >= NUMBER_OF_PASSES_BEFORE_SHOW;

	const show = canShow && !loading && !error && !hasSeenUserGuide && !dismissed && votedEnough;

	const stopAndMark = (e) => {
		// prevent profile menu from opening
		e.stopPropagation();

		markUserGuideAsSeen()
			.catch((err) => Sentry.captureException(err));
	};

	const dismissAndMarkAsSeen = (e) => {
		stopAndMark(e);

		trackDismissedGlobalPreferenceModal();

		setDismissed(true);
	};

	const handleLinkClick = (e) => {
		stopAndMark(e);

		setDismissed(true);
	};

	useLockBodyScroll(show);

	useTracking(show);

	if (!show) {
		return null;
	}

	return (
		<>
			{withFadeIn(<div
				className="global-preferences-tooltip-overlay"
				onClick={dismissAndMarkAsSeen}
			/>)}
			{withFadeIn(<div
				className="global-preferences-tooltip"
				role="alertdialog"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					className="global-preferences-tooltip-close"
					onClick={dismissAndMarkAsSeen}
				>
					<span className="icon" />
				</button>
				<div className="global-preferences-tooltip-notice">
					<Str>New Feature!</Str>
				</div>
				<ChecklistSvg className="global-preferences-tooltip-icon" />
				<Str className="global-preferences-tooltip-title">Match on what matters</Str>
				<Str className="global-preferences-tooltip-text">Choose who you want to see by setting your preferences.</Str>
				<Link
					onClick={handleLinkClick}
					className="global-preferences-tooltip-action"
					to={getGlobalPreferencesUrl(GP_SOURCE)}
				>
					<Str>Set Preferences</Str>
				</Link>
			</div>)}
		</>

	);
}

GlobalPreferenceTooltip.propTypes = {
	passCount: PropTypes.number.isRequired,
};

export default GlobalPreferenceTooltip;



// WEBPACK FOOTER //
// ./src/shared/components/GlobalPreferenceTooltip/index.jsx